import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ORGANIZATION_TYPE_LABELS } from 'src/app/shared/constants/organization-type-options';
import { OrganizationType } from 'src/app/core/data-models/organization-type.model';
import { getOrganizationTypeLabel } from 'src/app/core/utils/organization-type-utils';
import { OrganizationMembershipCardComponent } from '../organization-membership-card.component';
import { Organization } from 'src/app/core/data-models/organization.model';
import { OrganizationService } from 'src/app/core/data-services/organization.service';
import { Coordinates } from 'src/app/core/response-models/coordinates.model';
import { firstValueFrom } from 'rxjs';
import { GoogleMapsModule } from '@angular/google-maps';
import { FormsModule } from '@angular/forms';

interface OrganizationMember {
  id: string;
  profileId: string;
  role: string;
  firstName: string;
  lastName: string;
  email: string;
  joinedAt: string;
}

interface OrganizationStats {
  totalMembers: number;
  activeMembers: number;
  inactiveMembers: number;
  totalDrones: number;
  activeDrones: number;
  inactiveDrones: number;
  totalCertifications: number;
  validCertifications: number;
  expiringSoonCertifications: number;
  lastActivityAt: string | null;
}

@Component({
  selector: 'app-organization-overview-tab',
  standalone: true,
  imports: [CommonModule, GoogleMapsModule, FormsModule, OrganizationMembershipCardComponent],
  templateUrl: './organization-overview-tab.component.html',
  styleUrl: './organization-overview-tab.component.scss'
})
export class OrganizationOverviewTabComponent {
  @Input() organization: Organization | null = null;
  @Input() ownerProfile: OrganizationMember | null = null;
  @Input() chiefSupervisorProfile: OrganizationMember | null = null;
  @Input() stats: OrganizationStats | null = null;
  /** Bubbles a grant/revoke confirmation message up so the page can reload. */
  @Output() membershipChanged = new EventEmitter<string>();

  private readonly organizationService = inject(OrganizationService);

  organizationTypes = ORGANIZATION_TYPE_LABELS;
  lookingUpCoordinates = false;

  pendingCoordinates: Coordinates | null = null;
  lookupError: string | null = null;
  saveError: string | null = null;

  pendingManualCoordinates: Coordinates | null = null;
  savingCoordinates = false;

  mapOptions: google.maps.MapOptions = {
    disableDefaultUI: true,
    zoomControl: true,
    mapTypeId: 'hybrid',
  };

  getOrganizationTypeLabel = getOrganizationTypeLabel;

  updateManualCoordinate(field: 'lat' | 'lng', value: number | null): void {
    if (!this.pendingManualCoordinates || value == null) return;
    this.pendingManualCoordinates = { ...this.pendingManualCoordinates, [field]: value };
  }

  async lookupCoordinates() {
    if (!this.organization) return;

    this.lookupError = null;
    this.lookingUpCoordinates = true;

    try {
      const coords = await firstValueFrom(this.organizationService.geocode(this.organization.id));
      if (coords) {
        this.pendingCoordinates = coords;
      } else {
        this.startManualCoordinates();
      }
    } catch (error: unknown) {
      console.error('Geocode error:', error);
      this.startManualCoordinates();
    } finally {
      this.lookingUpCoordinates = false;
    }
  }

  async confirmedCoordinates() {
    const coords = this.pendingManualCoordinates ?? this.pendingCoordinates;
    if (!this.organization || !coords) return;
    
    this.savingCoordinates = true;
    this.saveError = null;

    try {
      await firstValueFrom(this.organizationService.updateCoordinates(this.organization.id, coords));
      this.organization.latitude = coords.lat;
      this.organization.longitude = coords.lng;

      this.pendingCoordinates = null;
      this.pendingManualCoordinates = null;
    } catch (error: unknown) {
      console.error('Save coordinates error:', error);
      this.saveError = 'Failed to save coordinates. Please try again.';
    } finally {
      this.savingCoordinates = false;
    }
  }

  declinedCoordinates() {
    if (this.savingCoordinates) return;
    
    this.lookupError = null;
    this.saveError = null;

    if (this.pendingCoordinates) {
      this.pendingCoordinates = null;
    }
    if (this.pendingManualCoordinates) {
      this.pendingManualCoordinates = null;
    }
  }

  /** Lookup failed: let the admin enter coordinates by hand, starting from the saved ones. */
  private startManualCoordinates(): void {
    this.lookupError = "We couldn't find coordinates for this address.";
    this.pendingCoordinates = null;
    this.pendingManualCoordinates = {
      lat: this.organization?.latitude ?? 0,
      lng: this.organization?.longitude ?? 0
    };
  }

  formatTimeAgo(dateString: string | null): string {
    if (!dateString) return 'Never';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    if (diffDays < 30) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  /** The masked EIN (XX-XXX1234) from the hub's `einLast4`, or from a full `ein` until the hub stops sending it. */
  maskEIN(organization: Organization): string {
    const last4 = organization.einLast4 ?? (organization.ein && organization.ein.length >= 4 ? organization.ein.slice(-4) : null);
    if (last4) {
      return `XX-XXX${last4}`;
    }
    return organization.ein ? 'XX-XXXXXXX' : 'Not provided';
  }
}
